"use client";

import type { ComponentConfig } from "@puckeditor/core";
import type { ComponentType } from "react";

type BlockControlProps = {
  blockAnchor?: string;
  blockColorScheme?: "inherit" | "porcelain" | "card" | "sage" | "muted";
  blockWidth?: "inherit" | "narrow" | "standard" | "wide" | "full";
  blockSpacing?: "inherit" | "compact" | "standard" | "generous";
  blockAlignment?: "inherit" | "left" | "center" | "right";
  blockVisibility?: "all" | "desktop" | "mobile";
  blockStructuredData?: boolean;
};

type StructuredDataKind = "faq" | "service-list" | "how-to" | "item-list";

type BlockControlOptions = {
  structuredData?: StructuredDataKind;
};

const blockControlFields = {
  blockColorScheme: {
    type: "select" as const,
    label: "Block · Colour scheme",
    options: [
      { label: "Use block default", value: "inherit" },
      { label: "Porcelain", value: "porcelain" },
      { label: "White card", value: "card" },
      { label: "Soft sage", value: "sage" },
      { label: "Muted surface", value: "muted" },
    ],
  },
  blockWidth: {
    type: "select" as const,
    label: "Block · Maximum width",
    options: [
      { label: "Use block default", value: "inherit" },
      { label: "Narrow reading width", value: "narrow" },
      { label: "Standard content", value: "standard" },
      { label: "Wide content", value: "wide" },
      { label: "Full width", value: "full" },
    ],
  },
  blockSpacing: {
    type: "select" as const,
    label: "Block · Outer spacing",
    options: [
      { label: "Use block default", value: "inherit" },
      { label: "Compact", value: "compact" },
      { label: "Standard", value: "standard" },
      { label: "Generous", value: "generous" },
    ],
  },
  blockAlignment: {
    type: "select" as const,
    label: "Block · Content alignment",
    options: [
      { label: "Use block default", value: "inherit" },
      { label: "Left", value: "left" },
      { label: "Centre", value: "center" },
      { label: "Right", value: "right" },
    ],
  },
  blockVisibility: {
    type: "select" as const,
    label: "Block · Device visibility",
    options: [
      { label: "All devices", value: "all" },
      { label: "Desktop and tablet only", value: "desktop" },
      { label: "Mobile only", value: "mobile" },
    ],
  },
  blockAnchor: {
    type: "text" as const,
    label: "Block · Navigation anchor",
  },
};

const blockControlDefaults: BlockControlProps = {
  blockAnchor: "",
  blockColorScheme: "inherit",
  blockWidth: "inherit",
  blockSpacing: "inherit",
  blockAlignment: "inherit",
  blockVisibility: "all",
};

function blockSchema(kind: StructuredDataKind, props: any) {
  if (kind === "faq") {
    const entries = Array.isArray(props.faqs) ? props.faqs : Array.isArray(props.items) ? props.items : [];
    const mainEntity = entries.flatMap((entry: any) => {
      const question = String(entry?.q || entry?.question || "").trim();
      const answer = String(entry?.a || entry?.answer || "").trim();
      return question && answer
        ? [{ "@type": "Question", name: question, acceptedAnswer: { "@type": "Answer", text: answer } }]
        : [];
    });
    return mainEntity.length > 0 ? { "@context": "https://schema.org", "@type": "FAQPage", mainEntity } : null;
  }

  if (kind === "service-list") {
    const services = Array.isArray(props.services) ? props.services : [];
    const itemListElement = services.flatMap((service: any, index: number) => {
      const name = String(service?.title || service?.name || "").trim();
      if (!name) return [];
      return [{
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Service",
          name,
          description: String(service?.body || service?.description || "").trim() || undefined,
          provider: { "@type": "Organization", name: "Shaaira Biocare" },
        },
      }];
    });
    return itemListElement.length > 0
      ? { "@context": "https://schema.org", "@type": "ItemList", name: props.heading || "Manufacturing services", itemListElement }
      : null;
  }

  if (kind === "how-to") {
    const steps = Array.isArray(props.steps) ? props.steps : [];
    const step = steps.flatMap((item: any, index: number) => {
      const name = String(item?.title || item?.name || "").trim();
      if (!name) return [];
      return [{
        "@type": "HowToStep",
        position: index + 1,
        name,
        text: String(item?.body || item?.description || "").trim() || undefined,
      }];
    });
    return step.length > 0
      ? { "@context": "https://schema.org", "@type": "HowTo", name: props.heading || "Manufacturing process", description: props.subheading || undefined, step }
      : null;
  }

  const entries = Array.isArray(props.categories)
    ? props.categories
    : Array.isArray(props.products)
      ? props.products
      : Array.isArray(props.items)
        ? props.items
        : [];
  const itemListElement = entries.flatMap((entry: any, index: number) => {
    const value = entry?.itemData || entry?.item || entry;
    const rawName = typeof value === "string" ? value : value?.title || value?.name || "";
    const name = String(rawName).trim();
    if (!name) return [];
    const url = value?.href || value?.link;
    return [{ "@type": "ListItem", position: index + 1, name, ...(url ? { url } : {}) }];
  });
  return itemListElement.length > 0
    ? { "@context": "https://schema.org", "@type": "ItemList", name: props.heading || props.productsHeading || "Content list", itemListElement }
    : null;
}

/** Adds the same safe layout controls to every normal content block. */
export function withContentBlockControls(config: ComponentConfig<any>, options: BlockControlOptions = {}): ComponentConfig<any> {
  const OriginalRender = config.render as ComponentType<any>;
  const structuredDataFields: NonNullable<ComponentConfig<any>["fields"]> = options.structuredData
    ? {
        blockStructuredData: {
          type: "radio" as const,
          label: "Block · Structured data",
          options: [
            { label: "Off (use page schema)", value: false },
            { label: "On", value: true },
          ],
        },
      }
    : {};

  return {
    ...config,
    fields: {
      ...(config.fields || {}),
      ...blockControlFields,
      ...structuredDataFields,
    },
    defaultProps: {
      ...(config.defaultProps || {}),
      ...blockControlDefaults,
      ...(options.structuredData ? { blockStructuredData: false } : {}),
    },
    render: function ManagedContentBlock(props: any) {
      const {
        blockAnchor = "",
        blockColorScheme = "inherit",
        blockWidth = "inherit",
        blockSpacing = "inherit",
        blockAlignment = "inherit",
        blockVisibility = "all",
        blockStructuredData = false,
      } = props;
      const schema = options.structuredData && blockStructuredData
        ? blockSchema(options.structuredData, props)
        : null;
      const anchor = typeof blockAnchor === "string" ? blockAnchor.trim().replace(/^#/, "") : "";

      const className = [
        "cms-block-frame",
        `cms-block-frame--scheme-${blockColorScheme}`,
        `cms-block-frame--width-${blockWidth}`,
        `cms-block-frame--spacing-${blockSpacing}`,
        `cms-block-frame--align-${blockAlignment}`,
        `cms-block-frame--visibility-${blockVisibility}`,
      ].join(" ");

      return (
        <div className={className} id={anchor || undefined}>
          {schema && (
            <script
              type="application/ld+json"
              data-schema-source="cms-block"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, "\\u003c") }}
            />
          )}
          <OriginalRender {...props} />
        </div>
      );
    },
  };
}
